<div class="flex flex-col">
  <div class="prose mt-6">
    <h1>Log Search</h1>
  </div>

  <div class="mt-8 space-y-4">
    <div class="flex gap-4">
      <div class="flex-1">
        <label class="label">
          <span class="label-text">Search Query</span>
        </label>
        <input
          type="text"
          class="input input-bordered w-full"
          [(ngModel)]="searchQuery"
          name="searchMessage"
          required
          [attr.minlength]="minSearchLength"
          autocomplete="off"
        />
        <p class="label-text-alt mt-1 text-base-content/70">
          Required - at least {{ minSearchLength }} characters
        </p>
      </div>
      <div>
        <label class="label">
          <span class="label-text">Start Date</span>
        </label>
        <input
          type="datetime-local"
          class="input input-bordered"
          [(ngModel)]="startDate"
          name="startDate"
          required
        />
      </div>
      <div>
        <label class="label">
          <span class="label-text">End Date</span>
        </label>
        <input
          type="datetime-local"
          class="input input-bordered"
          [(ngModel)]="endDate"
          name="endDate"
          required
        />
      </div>
    </div>

    <div class="flex gap-4">
      <button
        class="btn btn-primary"
        type="button"
        (click)="onSearch()"
        [disabled]="!canSearch"
      >
        Search
      </button>
      <button class="btn btn-neutral" (click)="onClear()" [disabled]="loading">
        Clear
      </button>
    </div>
  </div>

  @if (error) {
    <div class="alert alert-error mt-4">
      <span>{{ error }}</span>
    </div>
  }

  @if (loading) {
    <div class="mt-8 flex items-center justify-center">
      <span class="loading loading-spinner loading-lg"></span>
    </div>
  }

  @if (searchResults.length > 0) {
    <div class="mt-4">
      <!-- Filter Area -->
      <div
        class="mb-4 rounded-lg border border-base-content/20 bg-base-200 p-4"
      >
        <div class="mb-2 flex items-center justify-between">
          <h3 class="text-sm font-semibold">Filters</h3>
          @if (filters.length > 0) {
            <button class="btn btn-ghost btn-sm" (click)="clearFilters()">
              Clear All
            </button>
          }
        </div>
        @if (filters.length > 0) {
          <div class="flex flex-wrap gap-2">
            @for (filter of filters; track $index) {
              <div class="badge badge-primary gap-2">
                <span class="text-xs font-semibold">
                  {{
                    filter.type === 'ip'
                      ? 'IP'
                      : filter.type === 'user'
                        ? 'User'
                        : 'URL'
                  }}:
                </span>
                <span>{{ filter.value }}</span>
                <button
                  class="btn btn-circle btn-ghost btn-xs"
                  (click)="removeFilter($index)"
                >
                  ✕
                </button>
              </div>
            }
          </div>
        } @else {
          <p class="text-xs text-base-content/70">
            No filters applied. Click on IP, User, or URL values to add filters.
          </p>
        }
      </div>

      <div class="mb-2 text-sm text-base-content/70">
        Showing {{ (currentPage - 1) * itemsPerPage + 1 }} -
        {{ Math.min(currentPage * itemsPerPage, filteredResults.length) }} of
        {{ filteredResults.length }} results
        @if (filteredResults.length !== searchResults.length) {
          (filtered from {{ searchResults.length }} total)
        }
      </div>

      <div class="space-y-3">
        @for (logDetail of paginatedResults; track logDetail.request_id) {
          <div class="rounded-lg border border-base-content/20 bg-base-200 p-3">
            <div class="mb-2 flex flex-wrap items-center gap-3 text-sm">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th class="py-1">IP</th>
                    <th class="py-1">User</th>
                    <th class="whitespace-nowrap py-1">Time</th>
                    <th class="py-1">Method</th>
                    <th class="py-1">URL</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="w-[1%] py-1">
                      @if (
                        logDetail.client_ip && logDetail.client_ip !== 'N/A'
                      ) {
                        <div
                          class="tooltip tooltip-top relative inline-block"
                          data-tip="Click to filter by this IP"
                          (mouseenter)="
                            setHoveredItem('ip', logDetail.client_ip)
                          "
                          (mouseleave)="clearHoveredItem()"
                        >
                          <span
                            class="cursor-pointer text-primary hover:underline"
                            (click)="addFilter('ip', logDetail.client_ip)"
                          >
                            {{ logDetail.client_ip }}
                          </span>
                        </div>
                      } @else {
                        {{ 'N/A' }}
                      }
                    </td>
                    <td class="w-[1%] py-1">
                      @if (logDetail.email && logDetail.email !== 'N/A') {
                        <div
                          class="tooltip tooltip-top relative inline-block"
                          data-tip="Click to filter by this User"
                          (mouseenter)="setHoveredItem('user', logDetail.email)"
                          (mouseleave)="clearHoveredItem()"
                        >
                          <span
                            class="cursor-pointer text-primary hover:underline"
                            (click)="addFilter('user', logDetail.email)"
                          >
                            {{ logDetail.email }}
                          </span>
                        </div>
                      } @else {
                        {{ 'N/A' }}
                      }
                    </td>
                    <td class="w-[1%] whitespace-nowrap py-1">
                      {{ formatDate(logDetail.request_time) }}
                    </td>
                    <td class="w-[1%] py-1">
                      {{ logDetail.method || 'N/A' }}
                    </td>
                    <td class="py-1">
                      @if (logDetail.url && logDetail.url !== 'N/A') {
                        <div
                          class="tooltip tooltip-top relative inline-block"
                          data-tip="Click to filter by this URL"
                          (mouseenter)="setHoveredItem('url', logDetail.url)"
                          (mouseleave)="clearHoveredItem()"
                        >
                          <span
                            class="cursor-pointer text-primary hover:underline"
                            (click)="addFilter('url', logDetail.url)"
                          >
                            {{ logDetail.url }}
                          </span>
                        </div>
                      } @else {
                        {{ 'N/A' }}
                      }
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <details class="mb-2">
              <summary
                class="cursor-pointer text-xs text-primary hover:underline"
              >
                Show Details
              </summary>
              <div class="mt-2 space-y-2 pl-4 text-xs">
                <div>
                  <strong>Request ID:</strong> {{ logDetail.request_id }}
                </div>
                <div>
                  <strong>User ID:</strong> {{ logDetail.user_id || 'N/A' }}
                </div>
                @if (logDetail.header) {
                  <div>
                    <strong>Headers:</strong>
                    <pre class="mt-1 overflow-x-auto bg-base-300 p-1 text-xs">{{
                      logDetail.header
                    }}</pre>
                  </div>
                }
                @if (logDetail.body) {
                  <div>
                    <strong>Body:</strong>
                    <pre class="mt-1 overflow-x-auto bg-base-300 p-1 text-xs">{{
                      logDetail.body
                    }}</pre>
                  </div>
                }
              </div>
            </details>

            <div class="mt-2 border-t border-base-content/20 pt-2">
              @if (logDetail.truncated_logs) {
                <div class="mb-1 text-xs font-semibold text-warning">
                  * Logs are truncated due to number of returned results.
                </div>
              }
              <div
                class="mb-1 flex items-center justify-between text-xs font-semibold"
              >
                <span>Logs ({{ logDetail.logs.length }}):</span>
                @if (logDetail.logs && logDetail.logs.length > 10) {
                  <button
                    type="button"
                    class="btn btn-ghost btn-xs gap-1"
                    (click)="toggleLogsExpansion(logDetail)"
                  >
                    <span>
                      {{
                        areLogsExpanded(logDetail)
                          ? 'Hide logs'
                          : 'Show all logs'
                      }}
                    </span>
                    <span>
                      {{ areLogsExpanded(logDetail) ? '▲' : '▼' }}
                    </span>
                  </button>
                }
              </div>
              <div class="space-y-1">
                <table class="table table-sm">
                  @for (
                    log of getVisibleLogs(logDetail);
                    track log.created_at + log.message
                  ) {
                    <tbody>
                      <tr>
                        <td class="w-[1%] py-1 align-top text-base-content/70">
                          [{{ log.level }}]
                        </td>
                        <td
                          class="w-[1%] whitespace-nowrap py-1 align-top text-base-content/70"
                        >
                          {{ formatDate(log.created_at) }}
                        </td>
                        <td
                          class="flex-1 whitespace-pre-wrap py-1"
                          style="overflow-wrap: anywhere"
                          [innerHTML]="
                            highlightSearchText(log.message, searchQuery)
                          "
                        ></td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
            </div>
          </div>
        }
      </div>

      <!-- Pagination Controls -->
      @if (totalPages > 1) {
        <div class="mt-6 flex items-center justify-center gap-2">
          <button
            class="btn btn-sm"
            [class.btn-disabled]="currentPage === 1"
            (click)="previousPage()"
            [disabled]="currentPage === 1"
          >
            Previous
          </button>

          @if (getPageNumbers()[0] > 1) {
            <button class="btn btn-sm" (click)="goToPage(1)">1</button>
            @if (getPageNumbers()[0] > 2) {
              <span class="px-2">...</span>
            }
          }

          @for (page of getPageNumbers(); track page) {
            <button
              class="btn btn-sm"
              [class.btn-active]="currentPage === page"
              (click)="goToPage(page)"
            >
              {{ page }}
            </button>
          }

          @if (getPageNumbers()[getPageNumbers().length - 1] < totalPages) {
            @if (
              getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1
            ) {
              <span class="px-2">...</span>
            }
            <button class="btn btn-sm" (click)="goToPage(totalPages)">
              {{ totalPages }}
            </button>
          }

          <button
            class="btn btn-sm"
            [class.btn-disabled]="currentPage === totalPages"
            (click)="nextPage()"
            [disabled]="currentPage === totalPages"
          >
            Next
          </button>
        </div>
      }
    </div>
  }
</div>
